import { useState } from "react";
import { useNavigate } from "react-router-dom";

import loginService from "../Services/LoginService";
import { showErrorToast, showSuccessToast } from "../Constant/Notification";
import UserCheckService from "../../GlobalSetupComponents/Services/UserCheckService";
import { oldVersion } from "../../utils/storage";

const useLogin = () => {
  const navigate = useNavigate();
  

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formErrors, setFormErrors] = useState({
    email: false,
    password: false,
    general: "",
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSignout = () => {
    localStorage.clear();
    navigate("/");
    window.location.reload();
  };

  const checkUserStatusAndVersion = async (userUid) => {
    try {

      
      const body = {
        uid: userUid,
      };
      const response = await UserCheckService.isUserActive(body);
      const data = response.data;
      
      if (data) {
        localStorage.setItem("userActiveDetails", JSON.stringify(data));
        const newVersion = data?.webVersion;
        const currentVersion = oldVersion;
        const isUserActiveStatus = data?.userStatus === "active";

        // console.log("User status check results:");
        // console.log("- Web version (new):", newVersion);
        // console.log("- Web version (old):", currentVersion);
        // console.log("- User status:", data?.userStatus);
        // console.log("- Is user active:", isUserActiveStatus);

      
        if (!isUserActiveStatus) {
          console.log("User status check failed - user is not active, logging out...");
          handleSignout();
          return false;
        }

       
        if (currentVersion && newVersion !== currentVersion) {
          console.log("Version mismatch detected, logging out...");
          handleSignout();
          return false;
        }

        // If oldVersion is null (first login), just store the new version and continue
        if (!currentVersion) {
          console.log("First login detected, storing version:", newVersion);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching user status:", error);
      console.error("Error details:", error.response?.data || error.message);
      
      // Only logout if it's a 401/403 authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log("Authentication error detected, logging out...");
        handleSignout();
        return false;
      }
      
      // For other errors (network issues), don't logout
      return true;
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setFormErrors((prev) => ({ ...prev, email: false }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setFormErrors((prev) => ({ ...prev, password: false }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      showErrorToast("Email is not valid");
      return;
    }

    if (!email) {
      showErrorToast("Email is required");
      setFormErrors((prev) => ({ ...prev, email: true }));
      return;
    }

    if (!password) {
      showErrorToast("Password is required");
      setFormErrors((prev) => ({ ...prev, password: true }));
      return;
    }

    setIsLoading(true);
    setFormErrors({ email: false, password: false, general: "" });

    const loginData = {
      emailId: email.trim(),
      password: password.trim(),
    };

    try {
      const response = await loginService.loginUser(loginData);
      const userData = response.data;
      localStorage.setItem("userData", JSON.stringify(userData));
      
      // Check user status and version after successful login
      const userUid = userData?.user?.uid;
      if (userUid) {
        const isStatusValid = await checkUserStatusAndVersion(userUid);
        if (!isStatusValid) {
          // User status check failed, handleSignout already called
          return;
        }
      }
      
      const department = userData?.user?.department?.name || "";
      

      const { role } = userData.user;
      if (role === "Admin" || role === "CMO") {
        navigate("/dashboard");
      }

      if (department === "Sales") {
        if (role === "Lead Uploader") {
          navigate(`/lead-uploader`);
        } else {
          navigate(`${role}/dashboard`);
        }
      }
      if (department === "Management") {
        navigate(`/dashboard`);
      }
      if (department === "Revenue") {

        if( role === "Procurer" || role === "Frontend"  )
          {
            showErrorToast("Access Denied! You don't have access to Frontend/Procurer");
            navigate(`/no-access`);
            return;
          }

        if (role === "Rough Drafter") {
          navigate(`/procurement-raughDraft`);
        } else {
          navigate(`/propertyprocurement-list`);
        }
      }
      window.location.reload();
    } catch (error) {
      console.error("Login error:", error);
      showErrorToast(
        error?.response?.data?.message || "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    if (!email) {
      showErrorToast("Please enter your email address.");
      return;
    }

    try {
      const resetData = { email: email.trim() };
      const response = await loginService.resetPassword(resetData);
      if (response.status === 200) {
        setFormErrors((prev) =>
          prev ? { ...prev, email: "" } : { email: "" }
        );
        showSuccessToast("Password reset link sent to your email!");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      showErrorToast(error?.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    password,
    isLoading,
    formErrors,
    handleEmailChange,
    handlePasswordChange,
    handleLogin,
    navigate,
    handleForgotPassword,
  };
};

export default useLogin;
